import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft } from '@gravity-ui/icons';
import { Tooltip } from '@gravity-ui/uikit';
import { PaginationItem } from '@mui/material';
import type {
	MRT_ColumnDef,
	MRT_ColumnFiltersState,
	MRT_PaginationState,
	MRT_SortingState,
} from 'material-react-table';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';

import { BLOCK_BLANC, EDIT_BLANK, UNBLOCK_BLANC } from '../constants';

import { AccountUnblockModal } from './modals/account-unblock-modal';
import { CustomEstablishmentsFilter } from './custom-establishment-filter';
import { Localization_RU } from './localization';
import { AccountBlockModal, AccountEditModal } from './modals';

import { ReactComponent as ArrowsDownFilter } from '../../../assets/icons/arrow-down-filter.svg';
import { ReactComponent as ArrowsFilter } from '../../../assets/icons/arrows-filter.svg';

import styles from './styles.module.scss';

interface DataProps {
	data: Person[];
	onAdd: () => void;
	pageSize: number;
	setPageSize: React.Dispatch<React.SetStateAction<number>>;
	pageIndex: number;
	setPageIndex: React.Dispatch<React.SetStateAction<number>>;
	totalItems: number | undefined;
	sorting: MRT_SortingState;
	setSorting: React.Dispatch<React.SetStateAction<MRT_SortingState>>;
	columnFilters: MRT_ColumnFiltersState;
	setColumnFilters: React.Dispatch<React.SetStateAction<MRT_ColumnFiltersState>>;
	selectedFilters: string[];
}

interface Person {
	id: number;
	email: string;
	role: string;
	firstName: string;
	middleName: string;
	lastName: string;
	establishment: {
		id?: number;
		name?: string | null;
	} | null;
	imageUrl: string;
	region: {
		code?: number;
		fullname: string;
		guid?: string;
		name?: string;
	};
	specialty: string;
	active: boolean;
	createdDate: string;
}

export const AdminTable = ({
	data,
	onAdd,
	pageSize,
	setPageSize,
	pageIndex,
	setPageIndex,
	totalItems,
	sorting,
	setSorting,
	columnFilters,
	setColumnFilters,
	selectedFilters,
}: DataProps) => {
	const [active, setActive] = useState<Person[]>(data);

	useEffect(() => {
		setActive(data);
	}, [data]);

	const columns = useMemo<MRT_ColumnDef<Person>[]>(
		() => [
			{
				accessorFn: (row: Person) => `${row.firstName} ${row.middleName} ${row.lastName}`,
				header: 'Имя специалиста',
				enableHiding: false,
				enableColumnFilter: false,
			},
			{
				accessorKey: 'establishment.name',
				header: 'Учреждение',

				Header: ({ column }) => (
					<div style={{ display: 'flex', alignItems: 'center' }}>
						Учреждение
						<CustomEstablishmentsFilter column={column} selectedFilters={selectedFilters} />
					</div>
				),
				enableColumnFilter: false,
				enableSorting: false,
			},
			{
				accessorKey: 'email',
				header: 'Email',
				enableHiding: false,
				enableColumnFilter: false,
			},
			{
				accessorKey: 'createdDate',
				header: 'Дата',
				size: 10,
				enableHiding: false,
				enableColumnFilter: false,
			},
		],
		[data, selectedFilters],
	);

	const updateAccountStatus = (updatedAccount: Person) => {
		setActive((prevData) =>
			prevData.map((account) => (account.id === updatedAccount.id ? updatedAccount : account)),
		);
	};

	const [pagination, setPagination] = useState<MRT_PaginationState>({
		pageIndex: pageIndex,
		pageSize: pageSize,
	});

	useEffect(() => {
		setPageSize(pagination.pageSize);
		setPageIndex(pagination.pageIndex);
	}, [pagination.pageSize, pagination.pageIndex, setPageSize, setPageIndex]);

	const table = useMaterialReactTable({
		columns,
		data: active, //должно быть мемоизировано или стабильно (useState, useMemo и т.д.)
		manualPagination: true,
		manualSorting: true,
		manualFiltering: true,
		rowCount: totalItems,
		icons: {
			SyncAltIcon: (props: any) => <ArrowsFilter {...props} />,
			ArrowDownwardIcon: (props: any) => <ArrowsDownFilter {...props} />,
		},
		enableRowSelection: false, // отключить выбор строк
		enableTopToolbar: false,
		enableColumnActions: false, // отключить меню действий для столбцов
		enableRowActions: true, // рендер действий в строках
		positionActionsColumn: 'last', // действия в последнем столбце
		enableFilters: true, // отключить фильтрацию
		enableColumnOrdering: true,
		enableColumnDragging: false,
		enableGlobalFilter: false, // отключить глобальную фильтрацию
		localization: Localization_RU, // локализация
		enableStickyHeader: true, // липкий header
		enableStickyFooter: false, // липкий footer
		enableFullScreenToggle: false, // скрыть кнопку "на весь экран"
		positionPagination: 'both',
		state: { pagination, sorting, columnFilters },
		onPaginationChange: setPagination,
		onSortingChange: setSorting,
		onColumnFiltersChange: setColumnFilters,
		muiPaginationProps: {
			boundaryCount: 3,
			rowsPerPageOptions: [20, 40, 60, 80, 100],
			shape: 'rounded',
			variant: 'text',
			showLastButton: false,

			sx: {
				paddingRight: '20px',
			},
			size: 'large',
			renderItem: (item) => (
				<PaginationItem slots={{ previous: ChevronLeft, next: ChevronRight, first: ChevronsLeft }} {...item} />
			),
		},

		initialState: {
			density: 'compact',
			pagination: {
				pageIndex: 0,
				pageSize: 20,
			},
		},
		paginationDisplayMode: 'pages',
		layoutMode: 'semantic',
		muiTableBodyCellProps: ({ row }) => ({
			sx: {
				p: '6px 8px 5px',
				color: row.original.active ? 'inherit' : 'rgba(0, 0, 0, 0.4)',
			},
		}),
		muiTableBodyRowProps: ({ row }) => ({
			sx: {
				minHeight: '48px',
			},
		}),
		muiTablePaperProps: {
			elevation: 0,
		},

		renderRowActions: ({ row }) => (
			<div className={styles.blankTable__action}>
				{row.original.active ? (
					<>
						<Tooltip
							className={styles.blankTable__action__tooltip}
							content={EDIT_BLANK}
							placement="top"
							openDelay={500}
						>
							<div tabIndex={0}>
								<AccountEditModal account={row.original} onAdd={onAdd} key={row.original.id} />
							</div>
						</Tooltip>

						<Tooltip
							className={styles.blankTable__action__tooltip}
							content={BLOCK_BLANC}
							placement="top"
							openDelay={500}
						>
							<div tabIndex={0}>
								<AccountBlockModal
									account={row.original}
									onAdd={onAdd}
									updateAccountStatus={updateAccountStatus}
								/>
							</div>
						</Tooltip>
					</>
				) : (
					<Tooltip
						className={styles.blankTable__action__tooltip}
						content={UNBLOCK_BLANC}
						placement="top"
						openDelay={500}
					>
						<div tabIndex={0}>
							<AccountUnblockModal
								account={row.original}
								onAdd={onAdd}
								updateAccountStatus={updateAccountStatus}
							/>
						</div>
					</Tooltip>
				)}
			</div>
		),
	});

	return <MaterialReactTable table={table} />;
};
